import {
  CellValue,
  EntitySize,
  Freeze,
  IRange,
  Sheet,
  ValidationRule,
} from '../../../types';
import {
  DeleteSheet,
  SheetCommandType,
  CreateSheet,
  SheetCommand,
  FreezeGrid,
  HideUnhideGrid,
  ResizeGrid,
  CellInput,
  CellNumberFormat,
  AreaNumberFormat,
  CellStyle,
  AreaStyle,
  RemoveCellDataValidation,
  ApplyCellDataValidation,
} from '../../../types/commands/sheetCommands/sheetCommands';
// import { DataValidation } from '../../../types/sheet/dataValidation';
import { SheetManagementSet } from '../../../types/sheet/draft';
import { saveCreationEvent, saveDeletionEvent } from './common';

const saveActionPair = (
  set: SheetManagementSet,
  sheetId: string,
  action?: SheetCommand,
  undoAction?: SheetCommand
) => {
  if (action) {
    if (!set.present.sheetCommands) set.present.sheetCommands = new Map();
    if (!set.present.sheetCommands.has(sheetId))
      set.present.sheetCommands.set(sheetId, []);
    set.present.sheetCommands.get(sheetId)?.push(action);
  }

  if (undoAction) {
    if (!set.past.sheetCommands) set.past.sheetCommands = new Map();
    if (!set.past.sheetCommands.has(sheetId))
      set.past.sheetCommands.set(sheetId, []);
    set.past.sheetCommands.get(sheetId)?.unshift(undoAction); // TOOD: Question Do we need unshift or push?
  }
};

// #region grid
export const freezeEntityCommand = (
  set: SheetManagementSet,
  sheetId: string,
  action: FreezeGrid,
  oldFreeze: Freeze
) => {
  const inverse = {
    ...action,
    range: { start: oldFreeze.start, end: oldFreeze.end },
  };
  saveActionPair(set, sheetId, action, inverse);
};

export const hideUnhideEntityCommand = (
  set: SheetManagementSet,
  sheetId: string,
  action: HideUnhideGrid,
  revealedRanges?: IRange[]
) => {
  const inverse: HideUnhideGrid = {
    ...action,
    type:
      action.type === SheetCommandType.HideGrid
        ? SheetCommandType.UnhideGrid
        : SheetCommandType.HideGrid,
    ranges: revealedRanges ?? action.ranges, // For UnHidden we need to save revelead ranges in inverseAction
  };
  saveActionPair(set, sheetId, action, inverse);
};

export const resizeEntityCommand = (
  set: SheetManagementSet,
  sheetId: string,
  action: ResizeGrid,
  oldRangeSizes: EntitySize[]
) => {
  const inverse: ResizeGrid = {
    ...action,
    rangeSizes: oldRangeSizes,
    positionIndex: undefined,
    size: undefined,
  };

  saveActionPair(set, sheetId, action, inverse);
};

// #endregion

// #region cells
export const cellInputCommand = (
  set: SheetManagementSet,
  sheetId: string,
  action: CellInput,
  oldCellValue: CellValue | undefined
) => {
  const inverse = {
    ...action,
    value: oldCellValue?.toString() ?? '',
  };

  saveActionPair(set, sheetId, action, inverse);
};

export const cellNumberFormatCommand = (
  set: SheetManagementSet,
  sheetId: string,
  action: CellNumberFormat,
  oldFormats: AreaNumberFormat[]
) => {
  const inverse: CellNumberFormat = {
    ...action,
    formats: oldFormats,
  };
  saveActionPair(set, sheetId, action, inverse);
};

export const cellStyleCommand = (
  set: SheetManagementSet,
  sheetId: string,
  action: CellStyle,
  oldStyles: AreaStyle[]
) => {
  const inverse: CellStyle = {
    ...action,
    styles: oldStyles,
  };
  saveActionPair(set, sheetId, action, inverse);
};

export const applyDataValidationCommand = (
  set: SheetManagementSet,
  sheetId: string,
  action: ApplyCellDataValidation,
  oldRules: ValidationRule[],
  rulesToRemove: string[]
) => {
  saveActionPair(set, sheetId, action);

  if (oldRules.length) {
    const inverse: ApplyCellDataValidation = {
      ...action,
      rules: oldRules,
    };
    saveActionPair(set, sheetId, undefined, inverse);
  }

  if (rulesToRemove.length) {
    const inverse: RemoveCellDataValidation = {
      type: SheetCommandType.RemoveDataValidation,
      sheetId,
      ruleIds: rulesToRemove,
    };
    saveActionPair(set, sheetId, undefined, inverse);
  }
};

export const removeDataValidationCommand = (
  set: SheetManagementSet,
  sheetId: string,
  action: RemoveCellDataValidation,
  removedRules: ValidationRule[]
) => {
  const inverse: ApplyCellDataValidation = {
    type: SheetCommandType.ApplyDataValidation,
    sheetId,
    rules: removedRules,
  };

  saveActionPair(set, sheetId, action, inverse);
};
// #endregion

// #region crud
export const deleteSheetCommand = (
  set: SheetManagementSet,
  sheet: Sheet,
  exportedFeKey: string,
  groupId?: string,
  allowOpposite = true
) => {
  const action: DeleteSheet = {
    type: SheetCommandType.DeleteSheet,
    sheetId: sheet.id,
    groupId,
  };

  if (allowOpposite) {
    const reverse = createSheetCommand(
      set,
      sheet,
      exportedFeKey,
      groupId,
      false
    );

    saveDeletionEvent(set, action, reverse);

    return;
  }

  return action;
};

export const createSheetCommand = (
  set: SheetManagementSet,
  sheet: Sheet,
  exportedFeKey: string,
  groupId?: string,
  allowOpposite = true
) => {
  const action: CreateSheet = {
    type: SheetCommandType.CreateSheet,
    groupId,
    exportedFeKey,
    sheet,
  };

  if (allowOpposite) {
    const reverse = deleteSheetCommand(
      set,
      sheet,
      exportedFeKey,
      groupId,
      false
    );

    saveCreationEvent(set, action, reverse);

    return;
  }

  return action;
};
// #endregion
